import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { API_URL } from '../../../../config';
import InstructorStaffLayout from '../../../../components/InstructorStaffLayout';
import ConstrainedSelect from '../../../../components/ConstrainedSelect';
import { getMessage } from '../../../../data/locales';
import './CrearCurso.css'; // Reusing existing styles for consistency
import './ConfiguracionTests.css';

function courseOptionLabel(curso) {
  return curso.titulo || curso.nombre || String(curso._id);
}

function TestsConfigCourseSelect({ id, value, cursos, onChange }) {
  return (
    <div className="tests-config-course-field" data-testid="tests-config-course-field">
      <label htmlFor={id} className="tests-config-label">
        {getMessage("testsConfig.courseLabel")}
      </label>
      <ConstrainedSelect
        id={id}
        className="tests-config-select"
        value={value}
        ariaLabel={getMessage("testsConfig.courseLabel")}
        onChange={onChange}
        options={[
          { value: "", label: getMessage("testsConfig.coursePlaceholder") },
          ...cursos.map((curso) => ({
            value: String(curso._id),
            label: courseOptionLabel(curso),
          })),
        ]}
      />
    </div>
  );
}

TestsConfigCourseSelect.propTypes = {
  id: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  cursos: PropTypes.arrayOf(PropTypes.shape({
    _id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    titulo: PropTypes.string,
    nombre: PropTypes.string,
  }).isRequired).isRequired,
  onChange: PropTypes.func.isRequired,
};

function getTestsConfigBackPath(courseId) {
  if (typeof courseId === "string" && courseId.trim() !== "") {
    return `/admin/editar-curso/${encodeURIComponent(courseId.trim())}?tab=test`;
  }
  return "/GestionCursos";
}

function TestsConfigPageLayout({ title = "", children }) {
  return (
    <InstructorStaffLayout title={title} className="tests-config-page" hideSidebar>
      {children}
    </InstructorStaffLayout>
  );
}

TestsConfigPageLayout.propTypes = {
  title: PropTypes.string,
  children: PropTypes.node,
};

TestsConfigPageLayout.defaultProps = {
  title: "",
  children: null,
};

const ConfiguracionTests = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const autoRedirectDoneRef = useRef(false);
  const skipAutoRedirectFromRecovery = location.state?.skipTestsConfigAutoRedirect === true;
  const [curso, setCurso] = useState(null);
  const [cursos, setCursos] = useState([]);
  const [cursosLoading, setCursosLoading] = useState(true);
  const [cursosError, setCursosError] = useState(null);
  const [loading, setLoading] = useState(!!id);
  const [cursoLoadError, setCursoLoadError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedTest, setSelectedTest] = useState(null);
  const [formData, setFormData] = useState({
    tiempoPorPregunta: '',
    notaMinima: ''
  });

  const handleOpenModal = (test) => {
    setSelectedTest(test);
    // Extraer valores actuales o defaults
    // Nota: test.tiempo viene del mapeo anterior. Necesitamos asegurarnos de tener todos los datos.
    // test.tiempo es tiempoPorPregunta.
    // Necesitamos acceder al objeto original si queremos más campos, pero 'test' ya tiene lo básico.
    // Vamos a buscar la notaMinima en el objeto original si no está en el mapeo 'allTests'
    
    let notaMinima = '';
    const temaData = curso?.tests?.[test.bloque]?.[test.tema];
    if (temaData && !Array.isArray(temaData)) {
        notaMinima = temaData.notaMinima ?? '';
    }

    setFormData({
      tiempoPorPregunta: test.tiempo !== undefined && test.tiempo !== null ? test.tiempo : '',
      notaMinima: notaMinima
    });
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedTest(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = async () => {
    if (!selectedTest || !curso) return;

    try {
      // Construir el nuevo objeto de tests
      // Hacemos una copia profunda para no mutar el estado directamente
      const newTests = JSON.parse(JSON.stringify(curso.tests || {}));
      
      // Asegurar estructura
      if (!newTests[selectedTest.bloque]) newTests[selectedTest.bloque] = {};
      
      // Obtener datos actuales
      let currentData = newTests[selectedTest.bloque][selectedTest.tema];
      
      // Si era un array (formato antiguo), lo convertimos a objeto
      if (Array.isArray(currentData)) {
          currentData = { preguntas: currentData };
      } else if (!currentData) {
          currentData = { preguntas: [] };
      }

      // Actualizar valores
      // Convertir a número o null si está vacío
      const tiempo = formData.tiempoPorPregunta === '' ? null : Number(formData.tiempoPorPregunta);
      const nota = formData.notaMinima === '' ? null : Number(formData.notaMinima);

      if (tiempo !== null && (isNaN(tiempo) || tiempo <= 0)) {
        alert('El tiempo por pregunta debe ser un número positivo.');
        return;
      }
      if (nota !== null && (isNaN(nota) || nota < 0 || nota > 10)) {
        alert('La nota mínima debe estar entre 0 y 10.');
        return;
      }

      currentData.tiempoPorPregunta = tiempo;
      currentData.notaMinima = nota;

      // Guardar en la estructura
      newTests[selectedTest.bloque][selectedTest.tema] = currentData;

      // Enviar al backend
      const res = await fetch(`${API_URL}/api/admin/cursos/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          tests: newTests
        }),
        credentials: 'include'
      });

      if (res.ok) {
        // Actualizar estado local
        setCurso(prev => ({
            ...prev,
            tests: newTests
        }));
        alert('Configuración guardada correctamente');
        handleCloseModal();
      } else {
       let errorMsg = 'Desconocido';
        try {
          const errorData = await res.json();
          errorMsg = errorData.error || errorMsg;
        } catch { /* non-JSON response */ }
        alert(`Error al guardar: ${errorMsg}`);
      }
    } catch (error) {
      console.error("Error salvando configuración:", error);
      alert("Error de conexión al guardar");
    }
  };

  useEffect(() => {
    if (id) {
      setCursosLoading(false);
      return undefined;
    }
    const fetchCursos = async () => {
      setCursosLoading(true);
      setCursosError(null);
      try {
        const r = await fetch(`${API_URL}/cursos`, { credentials: 'include' });
        if (r.ok) {
          const data = await r.json();
          const normalized = Array.isArray(data)
            ? data
            : (Array.isArray(data?.cursos) ? data.cursos : []);
          setCursos(normalized);
        } else {
          setCursosError(getMessage("testsConfig.coursesLoadError"));
        }
      } catch (e) {
        console.error("Error cargando cursos:", e);
        setCursosError(getMessage("testsConfig.coursesConnectionError"));
      } finally {
        setCursosLoading(false);
      }
    };
    fetchCursos();
    return undefined;
  }, [id]);

  // Single assigned course: auto-open once per mount (ref). Location state skips auto-redirect after recovery navigation (ref resets on remount).
  useEffect(() => {
    if (autoRedirectDoneRef.current) return;
    if (id) return;
    if (skipAutoRedirectFromRecovery) return;
    if (cursosLoading) return;
    if (cursos.length !== 1) return;
    const cid = cursos[0]._id;
    if (!cid) return;
    autoRedirectDoneRef.current = true;
    navigate(`/admin/curso/${String(cid)}/tests-config`, { replace: true });
  }, [id, cursosLoading, cursos, navigate, skipAutoRedirectFromRecovery]);

  useEffect(() => {
    if (!id) {
      setCurso(null);
      setCursoLoadError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setCursoLoadError(null);
    const fetchCurso = async () => {
      try {
        const res = await fetch(`${API_URL}/api/admin/cursos/${id}`, {
          credentials: 'include'
        });

        if (res.ok) {
          const data = await res.json();
          setCurso(data);
        } else {
          setCurso(null);
          let msg = getMessage("testsConfig.loadCourseError");
          try {
            const err = await res.json();
            if (err?.error) msg = err.error;
          } catch {
            /* ignore */
          }
          if (res.status === 403) {
            msg = getMessage("testsConfig.noCoursePermission");
          }
          setCursoLoadError(msg);
        }
      } catch (error) {
        console.error("Error cargando curso:", error);
        setCurso(null);
        setCursoLoadError(getMessage("testsConfig.connectionError"));
      } finally {
        setLoading(false);
      }
    };
    fetchCurso();
  }, [id]);

  if (loading && id) {
    return (
      <TestsConfigPageLayout title={getMessage("testsConfig.title")}>
        <div style={{ padding: "2rem", textAlign: "center" }}>{getMessage("testsConfig.loading")}</div>
      </TestsConfigPageLayout>
    );
  }
  if (!id && !cursosLoading && cursos.length === 1 && !skipAutoRedirectFromRecovery) {
    return (
      <TestsConfigPageLayout>
        <p style={{ padding: "2rem", textAlign: "center" }}>{getMessage("testsConfig.openingCourse")}</p>
      </TestsConfigPageLayout>
    );
  }
  if (!id) {
    return (
      <TestsConfigPageLayout title={getMessage("testsConfig.title")}>
        <div style={{ width: '100%', maxWidth: '600px', margin: '0 auto' }}>
          <div className="tests-config-actions tests-config-actions--top" data-testid="tests-config-back-actions">
            <button
              type="button"
              className="tests-config-action-btn"
              onClick={() => navigate(getTestsConfigBackPath())}
            >
              {getMessage("testsConfig.back")}
            </button>
          </div>
          <p className="tests-config-lead" style={{ marginBottom: '20px' }}>
            {getMessage("testsConfig.selectCourseLead")}
          </p>
          {cursos.length > 0 ? (
            <TestsConfigCourseSelect
              id="curso-selector-empty"
              value=""
              cursos={cursos}
              onChange={(courseId) => {
                if (courseId) navigate(`/admin/curso/${courseId}/tests-config`);
              }}
            />
          ) : cursosLoading ? (
            <p>{getMessage("testsConfig.loadingCourses")}</p>
          ) : cursosError ? (
            <p>{cursosError}</p>
          ) : (
            <p>{getMessage("testsConfig.noCourses")}</p>
          )}
        </div>
      </TestsConfigPageLayout>
    );
  }
  if (!curso) {
    return (
      <TestsConfigPageLayout title={getMessage("testsConfig.title")}>
        <div style={{ padding: "2rem" }}>
          <p className="tests-config-error" style={{ marginBottom: 12 }}>{cursoLoadError || getMessage("testsConfig.courseNotFound")}</p>
          <button
            type="button"
            className="tests-config-action-btn"
            onClick={() =>
              navigate("/admin/configurar-tests", {
                replace: true,
                state: { skipTestsConfigAutoRedirect: true },
              })
            }
          >
            {getMessage("testsConfig.chooseCourse")}
          </button>
        </div>
      </TestsConfigPageLayout>
    );
  }

  // Transformar la estructura de Map a un array plano para mostrar
  // tests: Map<NombreBloque, Map<NombreTema, { preguntas: [], ... }>>
  const allTests = [];
  if (curso && curso.tests) {
    // Si viene como objeto (al enviarse por JSON)
    const bloques = (curso.tests && typeof curso.tests === 'object' && !Array.isArray(curso.tests)) ? curso.tests : {};
    
    Object.entries(bloques).forEach(([bloqueNombre, temas]) => {
      // Validar que temas sea un objeto y no null
      if (temas && typeof temas === 'object') {
        Object.entries(temas).forEach(([temaNombre, temaData]) => {
          // temaData puede ser array (legacy) o objeto { preguntas: ... }
          if (temaData) { // Validar que temaData existe
             const preguntas = Array.isArray(temaData) ? temaData : (temaData.preguntas || []);
             const tiempo = !Array.isArray(temaData) ? temaData.tiempoPorPregunta : null;
             
             allTests.push({
               titulo: `${bloqueNombre} - ${temaNombre}`,
               bloque: bloqueNombre,
               tema: temaNombre,
               preguntas: preguntas,
               tiempo: tiempo,
               tipo: 'Test Tema'
             });
          }
        });
      }
    });
  }

  return (
    <TestsConfigPageLayout title={getMessage("testsConfig.title")}>
      <div style={{ width: '100%', maxWidth: '800px', margin: '0 auto' }}>
        <div className="tests-config-actions tests-config-actions--top" data-testid="tests-config-back-actions">
          <button
            type="button"
            className="tests-config-action-btn"
            onClick={() => navigate(getTestsConfigBackPath(id))}
          >
            {getMessage("testsConfig.back")}
          </button>
        </div>
        <h2 style={{ fontSize: '1.25rem', marginBottom: '8px', color: '#ffffff' }}>{curso.titulo}</h2>
        <p className="tests-config-muted" style={{ marginBottom: '20px' }}>{getMessage("testsConfig.manageTests")}</p>
        
        <div className="tests-list-container" style={{ marginTop: '20px' }}>
          {allTests.length > 0 ? (
            <ul style={{ listStyle: 'none', padding: 0 }}>
              {allTests.map((test, index) => (
                <li key={index} className="tests-config-card">
                  <div>
                    <h3>{test.titulo}</h3>
                    <div className="tests-config-card-meta">
                      <span>⏱ {test.tiempo ? `${test.tiempo}s/preg` : 'Default'}</span>
                      <span style={{ margin: '0 10px' }}>|</span>
                      <span>📝 {test.preguntas?.length || 0} preguntas</span>
                      <span style={{ margin: '0 10px' }}>|</span>
                      <span>🏷️ {test.tipo}</span>
                    </div>
                  </div>
                  <button 
                    type="button"
                    className="tests-config-action-btn"
                    onClick={() => handleOpenModal(test)}
                  >
                    {getMessage("testsConfig.configure")}
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <div className="tests-config-empty">
              <p>{getMessage("testsConfig.noTests")}</p>
              <button
                type="button"
                className="tests-config-action-btn"
                style={{ marginTop: '10px' }}
                onClick={() => navigate(`/admin/editar-curso/${id}?tab=test`)}
              >
                {getMessage("testsConfig.createTestBlocks")}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Modal de Configuración */}
      {modalOpen && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#fff',
            padding: '25px',
            borderRadius: '8px',
            width: '90%',
            maxWidth: '500px',
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
          }}>
            <h2 className="tests-config-modal-title">
              Configurar Test: {selectedTest?.tema}
            </h2>
            
            <div style={{ marginBottom: '15px' }}>
              <label className="tests-config-modal-label">
                Tiempo por pregunta (segundos)
              </label>
              <input
                type="number"
                name="tiempoPorPregunta"
                value={formData.tiempoPorPregunta}
                onChange={handleInputChange}
                placeholder="Ej: 60 (Por defecto 50 segundos)"
                style={{
                  width: '100%',
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid #ccc'
                }}
              />
              <small className="tests-config-modal-help">Si se deja vacío, se usará el tiempo global del sistema.</small>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label className="tests-config-modal-label">
                Nota mínima para aprobar (0-10)
              </label>
              <input
                type="number"
                name="notaMinima"
                value={formData.notaMinima}
                onChange={handleInputChange}
                min="0"
                max="10"
                step="0.1"
                placeholder="Ej: 5 (Por defecto 5/10)"
                style={{
                  width: '100%',
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid #ccc'
                }}
              />
              <small className="tests-config-modal-help">Si se deja vacío, se usará el valor por defecto (5).</small>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                type="button"
                className="tests-config-modal-cancel"
                onClick={handleCloseModal}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="tests-config-modal-save"
                onClick={handleSave}
              >
                Guardar Cambios
              </button>
            </div>
          </div>
        </div>
      )}
    </TestsConfigPageLayout>
  );
};

export default ConfiguracionTests;
